import { QueryClient, useQuery } from "@tanstack/react-query";
import * as QueryKeys from "data";
import { fetchClipConfig, fetchClipGeometry } from "data/queries-common";
import { ClipConfiguration } from "./models";

// Presigned URLs change on every list refetch, so the key tracks the clip's last edit instead.
const clipGeometryQueryKey = (clip: ClipConfiguration) => [
  QueryKeys.clipGeometryKey,
  clip.id,
  clip.lastEditedAt ?? clip.date,
];

export interface ClipGeometryResult {
  geometry: GeoJSON.FeatureCollection | null;
}

export function useClipGeometry(clip: ClipConfiguration | undefined) {
  return useQuery<ClipGeometryResult>({
    queryKey: clip
      ? clipGeometryQueryKey(clip)
      : [QueryKeys.clipGeometryKey, "none"],
    queryFn: async () => ({
      geometry: await fetchClipGeometry(clip as ClipConfiguration),
    }),
    enabled: !!clip,
    staleTime: Infinity,
  });
}

export async function loadClipWithGeometry(
  queryClient: QueryClient,
  clipId: string,
): Promise<ClipConfiguration> {
  const clip = await fetchClipConfig(clipId);
  const result = await queryClient.fetchQuery<ClipGeometryResult>({
    queryKey: clipGeometryQueryKey(clip),
    queryFn: async () => ({
      geometry: await fetchClipGeometry(clip),
    }),
    staleTime: Infinity,
  });
  return { ...clip, geometry: result.geometry ?? undefined };
}
